import React, { useCallback, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { highlightText } from '@/features/shared/lib/utils';
import AuthorPopover from './AuthorPopover';


export default function PublicationCard({ item, showAltmetric = true, highlightQuery = '' }) {
  const navigate = useNavigate();
  const altmetricRef = useRef(null);
  const plumxRef = useRef(null);
  const id = item?.publication_id ?? item?.resource_id ?? item?.id;

  // PlumX는 SPA에서 DOM 변경 시 자동 재스캔을 안 하는 경우가 있어, 카드 렌더 후 ref 기준으로 wireUp 재호출
  useEffect(() => {
    const el = plumxRef.current;
    if (!el) return;

    // 이미 처리된 경우 중복 호출 방지(최소한)
    if (el.dataset && el.dataset.plumxWired === '1') return;

    const tryWireUp = () => {
      try {
        if (typeof window !== 'undefined'
          && window.__plumX
          && window.__plumX.widgets
          && window.__plumX.widgets.popup
          && typeof window.__plumX.widgets.popup.wireUp === 'function') {
          window.__plumX.widgets.popup.wireUp();
          if (el.dataset) el.dataset.plumxWired = '1';
        }
      } catch (e) {
        // ignore
      }
    };

    // 즉시 + 짧은 지연 후 한 번 더
    tryWireUp();
    const t = setTimeout(tryWireUp, 150);
    return () => clearTimeout(t);
  }, [item?.doi, item?.id]);

  const onClick = useCallback((e) => {
    // prevent default native navigation to avoid flicker
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    if (!id) return;

    // Check for Cmd (Mac) or Ctrl (Windows) key to open in a new tab
    if (e && (e.metaKey || e.ctrlKey)) {
      let homeUrl = (typeof rmVars !== 'undefined' && rmVars.homeUrl) ? rmVars.homeUrl : '';
      // If we are on port 3000 (dev mode), ensure homeUrl also uses port 3000
      if (typeof window !== 'undefined' && window.location.port === '3000' && homeUrl && !homeUrl.includes(':3000')) {
        homeUrl = homeUrl.replace(/(\/\/[^\/]+)/, '$1:3000');
      }
      const newTabUrl = `${homeUrl}/publications/${encodeURIComponent(id)}`;
      window.open(newTabUrl, '_blank');
      return;
    }

    navigate(`/publications/${encodeURIComponent(id)}`);
    try {
      if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
        setTimeout(() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' }), 0);
      }
    } catch (e) {
      // no-op
    }
  }, [id, navigate]);

  const meta = item?.meta || {};
  const title = item?.title || item?.publication_title || item?.resource_title || '(No title)';
  const authors = Array.isArray(item?.scholar)
    ? item.scholar.map(a => a?.scholar_name_eng || a?.author_name || a?.author_full_name || a?.author_abbr_name || a).filter(Boolean).join(', ')
    : (Array.isArray(item?.authors)
      ? item.authors.map(a => a?.name || a).filter(Boolean).join(', ')
      : (item?.author || ''));
  const source = item?.source || item?.journal_title || item?.conference || '';
  const date = item?.publish_year || item?.publish_date || '';

  // Format date to YYYY.MM (e.g., 2025.11). If month is unavailable, show YYYY only.
  const formatYearMonth = (val) => {
    if (!val) return '';
    const s = String(val).trim();
    if (!s) return '';

    let year = '';
    let month = '';

    // Try pattern with separators first (e.g., 2025-11-03, 2025/11, 2025.11.03)
    let m = s.match(/(\d{4})\D+(\d{1,2})/);
    if (m) {
      year = m[1];
      month = m[2];
    } else if (/^\d{6,8}$/.test(s)) {
      // Compact formats like 202511 or 20251103
      year = s.slice(0, 4);
      month = s.slice(4, 6);
    } else if (/^\d{4}$/.test(s)) {
      // Year only
      year = s;
    } else {
      // Fallback to Date parsing
      const d = new Date(s);
      if (!isNaN(d)) {
        year = String(d.getFullYear());
        month = String(d.getMonth() + 1);
      } else {
        return '';
      }
    }

    if (!year) return '';

    if (month) {
      const mi = Math.max(1, Math.min(12, parseInt(month, 10) || 0));
      if (mi >= 1) return `${year}.${String(mi).padStart(2, '0')}`;
    }
    return year;
  };

  const publisher = item?.publisher || '';
  const cite = item?.cited_count ?? 0;
  const vol = item?.volume ? `Vol. ${item.volume}` : '';
  const iss = item?.issue ? `No. ${item.issue}` : '';
  const pages = item?.pages || (item?.start_page && item?.end_page ? `${item.start_page}-${item.end_page}` : '');
  const dateText = formatYearMonth(date);

  const keywords = Array.isArray(item?.keywords) ? item.keywords : [];

  return (
    <li className="p-8 bg-white border border-[#E4E8F1] rounded-xl hover:shadow-[0px_4px_20px_rgba(0,0,0,0.1)] transition durarion-300">
      <button type="button" className={`block w-full text-left ${!showAltmetric ? 'flex-col' : ''}`}>
        <div>
          <div className="order-3 flex flex-col items-start gap-[6px] text-[#6C7687] md:flex-row md:items-center lg:flex-row flex-wrap lg:order-1 lg:mb-3">
            <p className="text-[14px] text-[#B4BCCC] font-medium mb-[2px] md:mb-0 md:mr-[2px] lg:hidden">
              Journal Ranking
            </p>
            {Array.isArray(item?.journal_ranking) && item.journal_ranking.map((rk, idx) => {
              const type = rk?.name || rk?.indicator_type || '';
              const year = rk?.year || rk?.indicator_year || '';
              const pctRaw = rk?.percent;
              let pctText = '';
              if (typeof pctRaw === 'number') {
                pctText = pctRaw.toFixed(pctRaw < 1 ? 2 : 2).replace(/\.00$/, '');
              } else if (typeof pctRaw === 'string') {
                pctText = pctRaw;
              }
              const cls = String(type).toLowerCase();
              return (
                <span key={idx} className={`type-${cls} mb-[6px] bg-[#F3F5F9] px-[8px] py-[6px] text-[13px] rounded`}>
                  {type} {year} <strong>Top {pctText}%</strong>
                </span>
              );
            })}
          </div>

          <div className="order-1 text-[18px] font-bold leading-[130%] mb-4 lg:order-2 lg:mb-[10px] cursor-pointer hover:underline underline-offset-2 decoration-1" onClick={onClick}>
            {/* 모바일: 배지 위 / 제목 아래. PC(lg~): 배지+제목 한 흐름(인라인), 제목이 두 줄이면 자연스럽게 두 줄 */}
            <div className="flex flex-col items-start gap-1 lg:block">
              {(meta.hcp === 'y' || meta.hp === 'y') && (
                <>
                  <span className="inline-flex flex-nowrap items-center gap-1 lg:align-middle mb-1">
                    {meta.hcp === 'y' && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-px pt-1 pb-1 rounded-full text-white text-[10px] font-bold leading-none bg-[#D4A017] shadow-sm shrink-0 mt-0 mb-0 align-middle">
                        {/* 트로피 아이콘 */}
                        <svg className="w-3 h-3 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                          <path d="M19 4h2a1 1 0 0 1 1 1v2a5 5 0 0 1-5 5h-.35A6.02 6.02 0 0 1 13 14.7V16h3a1 1 0 1 1 0 2h-3v1h2a1 1 0 1 1 0 2H7a1 1 0 1 1 0-2h2v-1H6a1 1 0 1 1 0-2h3v-1.3A6.02 6.02 0 0 1 7.35 12H7a5 5 0 0 1-5-5V5a1 1 0 0 1 1-1h2V3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v1Zm-2 0H7v3a5 5 0 0 0 10 0V4ZM4 6v1a3 3 0 0 0 3 3h.2A6 6 0 0 1 6 6H4Zm16 0h-2a6 6 0 0 1-1.2 4H17a3 3 0 0 0 3-3V6ZM10 18v1h4v-1h-4Z" />
                        </svg>
                        HCP
                      </span>
                    )}
                    {meta.hp === 'y' && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-px pt-1 pb-1 rounded-full text-white text-[10px] font-bold leading-none bg-[#F97316] shadow-sm shrink-0 mt-0 mb-0 align-middle">
                        {/* 불꽃 아이콘 */}
                        <svg className="w-3 h-3 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3.5 4 4 6.5-2.224 1.946-3.072 3.857-2 6 .5 1 1 1.62 1 3a2.5 2.5 0 0 1-2.5 2.5" />
                        </svg>
                        HP
                      </span>
                    )}
                  </span>
                  {' '}
                </>
              )}
              <span className="inline">{highlightQuery ? highlightText(title, highlightQuery) : title}</span>
              {typeof rmVars !== 'undefined' && rmVars?.isEditor && id && (
                <a
                  href={`${rmVars.homeUrl}/wp-admin/admin.php?page=resource&record_id=${id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center justify-center w-[18px] h-[18px] shrink-0 ml-2"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Edit Publication"
                >
                  <span
                    className="editor-edit"
                    style={{
                      width: '18px',
                      height: '18px',
                      filter: 'invert(11%) sepia(70%) saturate(6625%) hue-rotate(348deg) brightness(88%) contrast(99%)',
                      backgroundSize: '18px auto',
                      display: 'inline-block',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center center',
                      backgroundImage: `url(${rmVars.themeUrl}images/edit-regular.svg)`
                    }}
                  />
                </a>
              )}
            </div>
          </div>

          {item?.resource_title_translation && (
            <div className="hidden lg:block mb-[6px] text-[14px] font-bold text-[#B4BCCC] leading-[100%] order-3">
              {item.resource_title_translation}
            </div>
          )}

          {/* 하단 flex */}
          <div className={`flex justify-between items-end xl:items-start gap-[50px] w-full ${showAltmetric ? 'lg:mt-[14px]' : 'w-full'}`}>
            <div className=''>

              <div className="hidden order-4 text-[14px] text-[#6C7687] mb-[26px] lg:flex flex-wrap gap-x-3">
                {Array.isArray(item?.scholar) && item.scholar.length > 0 ? (
                  item.scholar.map((a, idx) => {
                    const nameEn = a?.scholar_name_eng || a?.author_full_name || a?.author_abbr_name || '';
                    const nameLocal = a?.author_name || '';
                    const displayName = nameEn && nameLocal ? `${nameEn}(${nameLocal})` : (nameEn || nameLocal || '');
                    return (
                      <AuthorPopover key={`${id}-${a?.scholar_id ?? 's'}-${idx}`} author={a}>{displayName}</AuthorPopover>
                    );
                  })
                ) : (
                  <div className="relative group flex items-center gap-1 pt-[4px]">
                    {authors}
                  </div>
                )}
              </div>

              <div
                className="order-2 flex flex-wrap items-center gap-2 text-[14px] text-[#6C7687] font-semibold leading-[100%] mb-4 pb-4 border-b border-b-[#E4E8F1] lg:order-5 lg:gap-x-4 md:border-none md:pb-0 md:mb-0"
              >
                {source && (
                  <div>
                    <span className="text-[#B4BCCC] font-medium mr-[5px]">Source</span>
                    {item?.journal_id ? (
                      <a href={`${rmVars.homeUrl}/journals/${encodeURIComponent(item.journal_id)}`} className="hover:underline underline-offset-2 decoration-1"><span>{highlightQuery ? highlightText(source, highlightQuery) : source}</span></a>
                    ) : (
                      <span>{highlightQuery ? highlightText(source, highlightQuery) : source}</span>
                    )}
                  </div>
                )}

                {source && <span className="inline-block w-[1px] h-[10px] bg-[#B4BCCC]"></span>}

                {(() => {
                  const volIssue = [item?.volume, item?.issue ? `(${item.issue})` : ''].filter(Boolean).join('');
                  return volIssue ? <span>{volIssue}</span> : null;
                })()}

                {(item?.volume || item?.issue) && <span className="inline-block w-[1px] h-[10px] bg-[#B4BCCC]"></span>}

                {pages && <span>pp.{pages}</span>}

                {pages && <span className="inline-block w-[1px] h-[10px] bg-[#B4BCCC]"></span>}

                {dateText && <span>{dateText}</span>}

                {dateText && <span className="inline-block w-[1px] h-[10px] bg-[#B4BCCC]"></span>}

                {publisher && (
                  <div>
                    <span className="text-[#B4BCCC] font-medium mr-[5px]">Publisher</span>
                    <span>{publisher}</span>
                  </div>
                )}

                {(publisher && (cite !== null && cite !== undefined)) && (
                  <span className="hidden w-[1px] h-[10px] bg-[#B4BCCC] lg:inline-block"></span>
                )}

                {(cite !== null && cite !== undefined) && (
                  <div className="hidden lg:inline-block">
                    <span className="text-[#B4BCCC] font-medium mr-[5px]">Citation Count</span>
                    <span>{String(cite)}</span>
                  </div>
                )}

                <span className="hidden w-[1px] h-[10px] bg-[#B4BCCC] lg:inline-block"></span>

                {Array.isArray(item?.registered_db) && item.registered_db.length > 0 && (
                  <div className="hidden text-[14px] font-semibold lg:flex flex-wrap items-center gap-[4px]">
                    <span className="text-[14px] text-[#B4BCCC] font-medium mr-[5px]">Registered DB</span>
                    {item.registered_db.map((db, idx) => {
                      const dbStr = String(db).toLowerCase();
                      const type = dbStr.includes('kci') ? 'kci' : dbStr;
                      return (
                        <span key={`${db}-${idx}`} className={`regist-tpye-${type} px-[5px] py-[2px] rounded`}>{db}</span>
                      );
                    })}
                  </div>
                )}
              </div>

              {keywords.length > 0 && (
                <div className="order-6 mt-4 flex flex-wrap gap-2">
                  {keywords.map((kw, idx) => (
                    <span key={idx} className="text-[13px] text-[#6C7687] bg-[#F3F5F9] px-2 py-1 rounded">
                      #{highlightQuery ? highlightText(kw, highlightQuery) : kw}
                    </span>
                  ))}
                </div>
              )}

              <div
                className="order-4 mt-4 flex flex-wrap items-center gap-[4px] text-[14px] font-semibold lg:hidden"
              >
                {Array.isArray(item?.registered_db) && item.registered_db.length > 0 && (
                  <>
                    <span className="w-full text-[14px] text-[#B4BCCC] font-medium md:w-auto md:mb-0 md:mr-[8px] lg:hidden">Registered DB</span>
                    {item.registered_db.map((db, idx) => {
                      const dbStr = String(db).toLowerCase();
                      const type = dbStr.includes('kci') ? 'kci' : dbStr;
                      return (
                        <span key={`${db}-${idx}`} className={`regist-tpye-${type} px-[5px] py-[2px] rounded`}>{db}</span>
                      );
                    })}
                  </>
                )}
              </div>

            </div>

            {showAltmetric && item?.doi && (
              <div className="shrink-0 hidden md:block">
                <div className="flex items-center justify-center gap-6 h-[80px]">
                  <div
                    ref={altmetricRef}
                    className="altmetric-embed flex items-center justify-center"
                    data-doi={item.doi}
                    data-badge-type="donut"
                    data-badge-width="60"
                    data-badge-popover="right"
                    data-hide-no-mentions="true"
                  />
                  <a
                    href={`https://plu.mx/plum/a/?doi=${item.doi}`}
                    className="plumx-plum-print-popup"
                    data-size="large"
                    data-popup="left"
                    data-hide-when-empty="true"
                  >
                  </a>
                </div>
              </div>
            )}
            {/*<div className="hidden shrink-0 w-[258px] lg:block">*/}
            {/*  <img*/}
            {/*    src={`${rmVars.themeUrl}images/thum01.png`}*/}
            {/*    alt="thum"/>*/}
            {/*</div>*/}

          </div>

        </div>

      </button>
    </li>
  );
}
